/** @file High-level share actions: create/revoke/list with URL builder. */
import { createShare, revokeShare, listSharesByPlay } from "../persistence/shares-repo.js";

/** @typedef {import("../types/schemas.js").ShareDoc} ShareDoc */

/**
 * Build the public share URL for a given id.
 * @param {string} shareId
 * @returns {string}
 */
export function buildShareUrl(shareId) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/share.html?id=${encodeURIComponent(shareId)}`;
}

/**
 * Create a share for a play. The shares-repo handles the atomic writeBatch
 * (shares/{id} set + play doc publicShareIds arrayUnion).
 * @param {string} ownerId
 * @param {string} playId
 * @returns {Promise<{ shareId: string, url: string }>}
 */
export async function createShareForPlay(ownerId, playId) {
  const shareId = await createShare(ownerId, playId);
  return { shareId, url: buildShareUrl(shareId) };
}

/**
 * Revoke an existing share. The shares-repo handles the atomic writeBatch
 * (shares/{id} revoked=true + play doc publicShareIds arrayRemove).
 * @param {string} _ownerId  (kept for API symmetry; repo reads owner off the share doc)
 * @param {string} _playId
 * @param {string} shareId
 * @returns {Promise<void>}
 */
export async function revokeShareForPlay(_ownerId, _playId, shareId) {
  await revokeShare(shareId);
}

/**
 * List only active (non-revoked) shares for a play.
 * @param {string} ownerId
 * @param {string} playId
 * @returns {Promise<ShareDoc[]>}
 */
export async function listActiveSharesForPlay(ownerId, playId) {
  const all = await listSharesByPlay(ownerId, playId);
  return all.filter((s) => !s.revoked);
}
